import * as React from 'react';

import * as Images from 'pos/Images';
import { Result } from 'core/infrastructure';
import { Preconditions, zDecimal } from 'core/infrastructure-next';
import { Command } from 'pos/common';
import * as SalesStore from '../SalesStore';
import { Permission, Res, TicketType, ServiceChargeDTO } from 'core/api';
import { Content, Header, PosScreen } from 'pos/ui';
import { Modal } from 'core/ui-next/modal';
import { z } from 'zod';
import { FormRef } from 'core/ui-next/controls/Form';
import { BasicButton } from 'core/ui-next';
import { VStack } from '@chakra-ui/react';
import { useControlsFor } from 'core/ui-next/controls';


type Props = {
	initialServiceCharge: ServiceChargeDTO
} & Modal.Actions<ServiceChargeDTO>;

const stateSchema = z.object({
	Percentage: zDecimal({
		decimalPlaces: 2,
		required: true,
		nonNegative: true
	}).refine(
		(val) => val >= 0 && val <= 100,
		{ message: Res.Msg.MSG_VALUE_IS_OUT_OF_RANGE(0, 100) }
	),
	ApplyTo: z.object({
		Name: z.string(),
		Value: z.number()
	}).nullable(),
	IgnoreTicketDiscounts: z.boolean(),
	Vat: z.object({
		Id: z.number(),
		SurchargePercentage: z.number(),
		VatPercentage: z.number(),
		Name: z.string(),
		Enabled: z.boolean(),
		VatType: z.number()
	})
});

type State = z.infer<typeof stateSchema>;


function EditTicketServiceChargeScreen(props: Props): React.ReactNode {
	const { initialServiceCharge, cancel, close } = props;

	const onAccept = async () => {
		const result = await formRef.current!.getValue();
		if (!result.ok)
			return;

		close({
			...initialServiceCharge,
			Percentage: result.value.Percentage,
			ApplyTo: result.value.ApplyTo!.Value,
			IgnoreTicketDiscounts: result.value.IgnoreTicketDiscounts,
			Vat: result.value.Vat!
		});
	};

	// Mapear TicketType[] a objetos { Name, Value } para el Select
	const applyToOptions = initialServiceCharge.ExistingTicketType.map(ticketType => ({
		Name: TicketType.toString(ticketType),
		Value: ticketType
	}));

	const formRef = React.useRef<FormRef<State>>(null);

	const { Form, DecimalInput, Select, CheckBox, } = useControlsFor<State>();

	const initialState: State = {
		Percentage: initialServiceCharge.Percentage,
		ApplyTo: initialServiceCharge.ApplyTo !== null && initialServiceCharge.ApplyTo !== undefined
			? {
				Name: TicketType.toString(initialServiceCharge.ApplyTo),
				Value: initialServiceCharge.ApplyTo
			}
			: {
				Name: TicketType.toString(initialServiceCharge.ExistingTicketType?.[0]!),
				Value: initialServiceCharge.ExistingTicketType?.[0]!
			},
		Vat: initialServiceCharge.Vat || (initialServiceCharge.ExistingTaxes?.[0])!,
		IgnoreTicketDiscounts: initialServiceCharge.IgnoreTicketDiscounts
	};

	return <PosScreen size={'small'} screenId={'EDIT_TICKET_SERVICE_CHARGE'} useKeyPad={true}>
		<Header title={Res.Web.EDIT_TICKET_SERVICE_CHARGE_TEXT()} image={Images.ServiceCharge} onClose={cancel} />
		<Content>
			<VStack width={'full'} paddingY={4.5} paddingX={6} gap={6} overflowY={'auto'} height={'full'}>
				<Form ref={formRef} initialValue={initialState} schema={stateSchema} style={{ width: '100%' }}>
					<VStack width={'full'} gap={6}>
						<DecimalInput
							label={Res.Web.EDIT_TICKET_SERVICE_CHARGE_PERCENTAGE_LBL()}
							linkTo={'Percentage'}
							allowNegative={false}
							precision={2}
							min={0}
							max={100}
							placeholder={'0,00 %'}
							required
						/>
						<Select
							label={Res.Web.EDIT_TICKET_SERVICE_CHARGE_APPLY_TO_LBL()}
							linkTo={'ApplyTo'}
							options={applyToOptions}
							renderOption={opt => opt?.Name ? opt.Name : ''}
							valueGetter={opt => opt?.Name ? opt.Name : ''}
							placeholder={''}
							required
						/>
						<Select
							label={Res.Web.EDIT_TICKET_SERVICE_CHARGE_VAT_LBL()}
							linkTo={'Vat'}
							options={initialServiceCharge.ExistingTaxes}
							renderOption={opt => opt?.Name ? opt.Name : ''}
							valueGetter={opt => opt?.Name ? opt.Name : ''}
							placeholder={''}
							required
						/>
						<CheckBox size={'sm'} label={Res.Web.EDIT_TICKET_SERVICE_CHARGE_TICKET_DISCOUNTS()} linkTo={'IgnoreTicketDiscounts'} cursor={'pointer'} />
					</VStack>
				</Form>
				<BasicButton type={'success'} text={Res.Common.OK_BTN()} onClick={onAccept} />
			</VStack>
		</Content>
	</PosScreen>;
}


declare global {
	interface CommandType {
		editTicketServiceCharge: () => Promise<Result>
	}
}


Command.register('editTicketServiceCharge', Preconditions.ensure(Permission.Ticket_EditServiceCharge), async (): Promise<Result> => {
	const charge = await SalesStore.getServiceCharge();
	if (charge === null)
		return Result.error();

	const serviceChargeResult = await Modal.show(EditTicketServiceChargeScreen, { initialServiceCharge: charge });
	if (!serviceChargeResult.ok)
		return Result.error();

	await SalesStore.setServiceCharge(serviceChargeResult.value);
	return Result.ok();
});
